import { useAuthStore } from '../store/shared/auth-store';
import { t } from '@/locales';

export default class AuthorizeService {
	static #can(permission, arg = {}) {
		if (!permission) return false;

		const authStore = useAuthStore();
		const userPermissions = authStore.permissions;

		if (arg && Object.keys(arg).length > 0) {
			permission = this.#injectArguments(permission, arg);
		}

		if (userPermissions.has(permission)) {
			return true;
		}

		return this.#checkWildcardPermissions(permission, userPermissions);
	}

	static #checkWildcardPermissions(permission, userPermissions) {
		if (!permission) return false;

		const parts = permission.split('.');

		if (parts.length === 1) {
			return false;
		}

		const wildcardPatterns = this.#generateWildcardPatterns(parts);

		for (const pattern of wildcardPatterns) {
			if (userPermissions.has(pattern)) {
				return true;
			}
		}

		return false;
	}

	static #generateWildcardPatterns(parts) {
		const patterns = [];

		for (let i = parts.length - 1; i >= 0; i--) {
			const pattern = [...parts];
			pattern[i] = '*';
			patterns.push(pattern.join('.'));
		}

		return patterns;
	}

	static #injectArguments(permission, arg) {
		if (!permission) return false;

		if (!arg || Object.keys(arg).length === 0 || !permission.includes('{')) {
			return permission;
		}

		for (const [key, value] of Object.entries(arg)) {
			const param = `{${key}}`;
			if (permission.includes(param)) {
				permission = permission.replace(param, value);
			}
		}

		return permission;
	}

	/**
	 * @param {[string, Record<string, any>][]} permissions
	 * @returns
	 */
	static #hasAnyPermission(permissions) {
		if (!permissions) return false;

		for (const permission of permissions) {
			if (this.#can(permission[0], permission[1])) {
				return true;
			}
		}
		return false;
	}

	/**
	 * @param {[string, Record<string, any>][]} permissions
	 * @returns
	 */
	static #hasAllPermissions(permissions) {
		if (!permissions) return false;

		for (const permission of permissions) {
			if (!this.#can(permission[0], permission[1])) {
				return false;
			}
		}
		return true;
	}

	static #getAllPermissions() {
		const authStore = useAuthStore();
		return Array.from(authStore.permissions);
	}

	static can(permission, arg = {}) {
		return this.#can(permission, arg);
	}

	static cannot(permission, arg = {}) {
		return !this.#can(permission, arg);
	}

	static canAll(permissions) {
		return this.#hasAllPermissions(permissions);
	}

	static canAny(permissions) {
		return this.#hasAnyPermission(permissions);
	}

	static cannotAll(permissions) {
		return !this.#hasAllPermissions(permissions);
	}

	static cannotAny(permissions) {
		return !this.#hasAnyPermission(permissions);
	}

	static unauthorizedMessage(permission, arg = {}, fallback = '') {
		return this.#can(permission, arg) ? fallback : t('not-authorized', { permission });
	}

	static unauthorizedMessageAll(permissions, fallback = '') {
		const firstPermissionName = permissions[0][0];
		return this.#hasAllPermissions(permissions)
			? fallback
			: t('not-authorized', { permission: firstPermissionName });
	}
}
